import cn from 'classnames';
import keys from 'lodash/keys';

import NutritionTableContentRow from 'components/mealDetails/nutritionFacts/NutritionTableContentRow';
import {
  getNutritionFactInfoLegend,
  VITAMIN_INFO_LEGEND,
  NO_PERCENT_DATA_REQUIRED,
  ADDED_SUGARS,
} from 'lib/constants';

const NutritionTableContentRows = ({
  needsAllNutritionDetails,
  dailyPercentAsPackaged,
  dailyPercentAsPrepared,
  dailyValueAsPackaged,
  dailyValueAsPrepared,
  isTempo,
}) => {
  const displayValues = isTempo ? dailyValueAsPrepared : dailyValueAsPackaged;
  const displayPercentages = isTempo ? dailyPercentAsPrepared : dailyPercentAsPackaged;

  const thBaseStyle = 'px-[3px] pb-[2px] pt-[3px] align-baseline leading-none helveticaBlack';
  const blackBorderStyle = 'border border-solid border-black-100';

  return (
    <>
      <tbody>
        <tr
          className={cn('p-0', {
            '[&>*:last-child]:w-1/3 [&>*:last-child]:border-r-0 [&>*:last-child]:pr-0':
              needsAllNutritionDetails,
            'border-t-0': !needsAllNutritionDetails,
          })}
        >
          <th
            className={cn(thBaseStyle, 'text-right', {
              [blackBorderStyle]: needsAllNutritionDetails,
              'w-1/3 border-l-0': needsAllNutritionDetails,
            })}
            scope="col"
          >
            <span className="sr-only">Nutrient type</span>
          </th>

          <th
            className={cn(thBaseStyle, 'border-r-0 text-left', {
              [blackBorderStyle]: needsAllNutritionDetails,
            })}
            scope="col"
          >
            <span className="sr-only">
              {`Nutrient amount by weight, ${isTempo ? 'as prepared' : 'packaged'}`}
            </span>
          </th>
          <th
            className={cn(thBaseStyle, 'border-l-0 text-right', {
              [blackBorderStyle]: needsAllNutritionDetails,
            })}
            scope="colgroup"
          >
            <span className="sr-only">
              {`Nutrient amount by percent of daily requirement, ${isTempo ? 'as prepared' : 'packaged'}`}
            </span>
            <span
              className="whitespace-nowrap"
              title="The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice."
            >
              % {needsAllNutritionDetails ? 'DV' : 'Daily Value'}
              <sup className="align-bottom text-[100%]">*</sup>
            </span>
          </th>

          {needsAllNutritionDetails && (
            <>
              <th className={cn(thBaseStyle, blackBorderStyle, 'border-r-0 text-left')} scope="col">
                <span className="sr-only">Nutrient amount by weight, as prepared</span>
              </th>
              <th
                className={cn(thBaseStyle, blackBorderStyle, 'border-l-0 text-right')}
                scope="col"
              >
                <span className="sr-only">
                  Nutrient amount by percent of daily requirement, as prepared
                </span>
                <span
                  className="whitespace-nowrap"
                  title="The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice."
                >
                  % DV<sup className="align-bottom text-[100%]">*</sup>
                </span>
              </th>
            </>
          )}
        </tr>
        {getNutritionFactInfoLegend(!needsAllNutritionDetails).map((group, idx) =>
          keys(group).map(slug => {
            if (slug === 'groupTitle') return null;
            return (
              <NutritionTableContentRow
                displayPercent={!NO_PERCENT_DATA_REQUIRED.includes(slug)}
                displayPreparedNutritionInfo={needsAllNutritionDetails}
                isHeader={group.groupTitle === slug}
                key={slug}
                percentAsPackaged={displayPercentages[slug]}
                percentAsPrepared={dailyPercentAsPrepared[slug]}
                rowClass={cn({
                  'border-t border-solid border-black-100': !needsAllNutritionDetails,
                })}
                title={group[slug]}
                valueAsPackaged={displayValues[slug]}
                valueAsPrepared={dailyValueAsPrepared[slug]}
                addedSugarsRow={slug === ADDED_SUGARS}
                isLastRow={idx === getNutritionFactInfoLegend(!needsAllNutritionDetails).length - 1}
              />
            );
          })
        )}
      </tbody>
      <tbody>
        {VITAMIN_INFO_LEGEND.map(group =>
          keys(group).map(slug => (
            <NutritionTableContentRow
              displayPercent={true}
              displayPreparedNutritionInfo={needsAllNutritionDetails}
              isHeader={false}
              key={slug}
              percentAsPackaged={displayPercentages[slug]}
              percentAsPrepared={dailyPercentAsPrepared[slug]}
              title={group[slug]}
              valueAsPackaged={displayValues[slug]}
              valueAsPrepared={dailyValueAsPrepared[slug]}
              addedSugarsRow={false}
              thStyles={needsAllNutritionDetails ? '!pl-[0.2em]' : ''}
              isVitaminInfo={true}
            />
          ))
        )}
      </tbody>
    </>
  );
};

NutritionTableContentRows.propTypes = {
  needsAllNutritionDetails: PropTypes.bool.isRequired,
  dailyPercentAsPackaged: PropTypes.object.isRequired,
  dailyPercentAsPrepared: PropTypes.object.isRequired,
  dailyValueAsPackaged: PropTypes.object.isRequired,
  dailyValueAsPrepared: PropTypes.object.isRequired,
  isTempo: PropTypes.bool.isRequired,
};

export default NutritionTableContentRows;
